import { CoreEarnRateAbi, CoreTotalStakedAbi } from '../abis/core'
import { TotalSupplyAbi } from '../abis/shared'
import type { LstRegistryEntry } from '../registry'
import { ONE_E18, toBigInt, type LstReader } from './shared'

/** stCORE's `Earn.getCurrentExchangeRate()` is scaled by 1e6. */
const CORE_RATE_DENOM = 10n ** 6n

/**
 * stCORE reader (Core chain 1116).
 *
 * The rate lives on the `Earn` contract (address in
 * `readerExtras.coreEarn`), not the token. `getCurrentExchangeRate()`
 * returns a **1e6-scaled** CORE-per-stCORE rate, so it is rescaled to
 * the 1e18 convention. `totalAssets = totalSupply * rate / 1e6`.
 */
export const readerCoreEarnRate = (entry: LstRegistryEntry): LstReader => {
  const earn = entry.readerExtras?.coreEarn ?? entry.address
  return {
    calls: [
      { address: earn, name: 'getCurrentExchangeRate', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
    ],
    abis: [CoreEarnRateAbi, TotalSupplyAbi],
    parse: ([rate, supply]) => {
      const r = toBigInt(rate)
      const totalSupply = toBigInt(supply)
      if (r === undefined || r === 0n || totalSupply === undefined) {
        return undefined
      }
      return {
        totalAssets: (totalSupply * r) / CORE_RATE_DENOM,
        totalSupply,
        exchangeRate: (r * ONE_E18) / CORE_RATE_DENOM,
      }
    },
  }
}

/**
 * SCORE / dualCORE reader (Core chain 1116).
 *
 * Value-accruing share tokens: `exchangeRate = totalStaked / totalSupply`
 * (1e18-scaled). `totalStaked` is read from `readerExtras.coreStakeContract`
 * when set (dualCORE → its b14g `coreVault`), otherwise from the token
 * itself (SCORE is its own staking contract). `totalSupply` is always the
 * share token's.
 */
export const readerCoreStakedRatio = (entry: LstRegistryEntry): LstReader => {
  const stakeContract = entry.readerExtras?.coreStakeContract ?? entry.address
  return {
    calls: [
      { address: stakeContract, name: 'totalStaked', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
    ],
    abis: [CoreTotalStakedAbi, TotalSupplyAbi],
    parse: ([staked, supply]) => {
      const totalStaked = toBigInt(staked)
      const totalSupply = toBigInt(supply)
      if (
        totalStaked === undefined ||
        totalSupply === undefined ||
        totalSupply === 0n
      ) {
        return undefined
      }
      return {
        totalAssets: totalStaked,
        totalSupply,
        exchangeRate: (totalStaked * ONE_E18) / totalSupply,
      }
    },
  }
}
